import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Excalidraw } from "@excalidraw/excalidraw";
import "@excalidraw/excalidraw/index.css";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import { useAuth } from "../auth/msalClient";
import * as backendClient from "../api/backendClient";
import { restoreScene } from "../utils/sceneHelpers";
import { Button } from "@excalidraw-ms/shared-ui";
import ReadOnlyMainMenu from "../components/ReadOnlyMainMenu";
import AboutDialog from "../components/AboutDialog";
import { useThemePreference } from "../hooks/useThemePreference";
import { useAppActivity } from "../hooks/useAppActivity";

export default function ShareViewPage() {
  const { blobId } = useParams<{ blobId: string }>();
  const navigate = useNavigate();
  const { account, loading, signIn, signOut, getToken, hasBackendAccess } = useAuth();
  const { resolvedTheme } = useThemePreference({
    accountId: account?.homeAccountId,
    getToken,
    syncEnabled: hasBackendAccess,
  });
  const [initialData, setInitialData] = useState<{
    elements: ExcalidrawElement[];
    appState: Partial<AppState>;
    files: BinaryFiles;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  useAppActivity({
    accountId: account?.homeAccountId,
    enabled: hasBackendAccess && !loading,
    getToken,
    drawingReady: !!initialData && !error,
  });

  useEffect(() => {
    if (!account || !blobId) return;

    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const result = await backendClient.loadSharedScene(token, blobId);
        if (cancelled) return;
        setInitialData(restoreScene(result.scene));
        setExpiresAt(result.expiresAt ?? null);
      } catch (err) {
        if (cancelled) return;
        console.error("[ShareViewPage] Failed to load shared scene:", err);
        setError("Failed to load shared drawing.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [account, blobId, getToken]);

  const bannerText = useMemo(() => {
    if (!expiresAt) return "Read-only view";
    return `Read-only view — this link expires ${new Date(expiresAt).toLocaleDateString()}`;
  }, [expiresAt]);

  const handleOpenInEditor = useCallback(() => {
    navigate(`/?importShare=${encodeURIComponent(blobId ?? "")}`);
  }, [blobId, navigate]);

  if (loading) {
    return <CenteredMessage>Signing in…</CenteredMessage>;
  }

  if (!account) {
    return (
      <CenteredMessage>
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>Open shared drawing</h1>
          <p style={{ color: "var(--color-text-secondary)", marginBottom: 24 }}>
            Sign in with your MSIT account to view this shared link.
          </p>
          <Button variant="primary" onClick={signIn}>
            Sign in with Microsoft
          </Button>
        </div>
      </CenteredMessage>
    );
  }

  if (error) {
    return (
      <CenteredMessage>
        <div style={{ textAlign: "center" }}>
          <p style={{ color: "var(--exms-danger-text)", marginBottom: 16 }}>{error}</p>
          <Button variant="primary" onClick={() => navigate("/")}>
            Back to editor
          </Button>
        </div>
      </CenteredMessage>
    );
  }

  if (!initialData) {
    return <CenteredMessage>Loading shared drawing…</CenteredMessage>;
  }

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={bannerStyle}>
        <span>{bannerText}</span>
        <Button variant="primary" onClick={handleOpenInEditor} data-app-usage-surface="reading">
          Open in editor
        </Button>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <Excalidraw initialData={initialData} viewModeEnabled={true} theme={resolvedTheme}>
          <ReadOnlyMainMenu
            account={account}
            onOpenInEditor={handleOpenInEditor}
            onAbout={() => setShowAboutDialog(true)}
            onSignOut={signOut}
          />
        </Excalidraw>
      </div>

      {showAboutDialog && <AboutDialog onClose={() => setShowAboutDialog(false)} />}
    </div>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

const bannerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "10px 16px",
  borderBottom: "1px solid var(--color-border)",
  background: "var(--island-bg-color)",
  fontFamily: "system-ui, sans-serif",
  fontSize: 14,
};
